import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, message, Alert } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/lib/upload/interface';
import CustomModal from '../../../../components/Common/CustomModal';
import { uploadAbsenceProof } from '../../../../services/absenceService';
import { Absence } from '../../../../types/absenceTypes';

const { Dragger } = Upload;

interface AbsenceProofUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  absence: Absence;
  onSuccess: () => void;
}

const AbsenceProofUploadModal: React.FC<AbsenceProofUploadModalProps> = ({
  isOpen,
  onClose,
  absence,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Get max file size from environment variable or use default 2MB
  const maxSizeMb = process.env.REACT_APP_MAX_UPLOAD_SIZE_MB ? parseInt(process.env.REACT_APP_MAX_UPLOAD_SIZE_MB) : 2;
  const maxSizeBytes = maxSizeMb * 1024 * 1024;

  // Handle file upload
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t('please_select_a_file'));
      return;
    }

    try {
      setLoading(true);
      const file = fileList[0].originFileObj as File;
      await uploadAbsenceProof(absence.id as string, file);
      
      message.success(t('proof_uploaded_successfully'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error uploading proof:', error);
      message.error(t('failed_to_upload_proof'));
    } finally {
      setLoading(false);
    }
  };

  // Validate file before upload
  const beforeUpload = (file: File) => {
    // Log file details for debugging
    console.log('File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      maxSizeBytes: maxSizeBytes,
      sizeInMB: (file.size / (1024 * 1024)).toFixed(2) + 'MB'
    });
    
    // Check file type
    const isPdf = file.type === 'application/pdf';
    if (!isPdf) {
      message.error(t('only_pdf_files_are_allowed'));
      return Upload.LIST_IGNORE;
    }
    
    // Check file size
    const isLessThanMaxSize = file.size <= maxSizeBytes;
    if (!isLessThanMaxSize) {
      message.error(t('file_size_exceeds_limit', { size: (file.size / (1024 * 1024)).toFixed(2), maxSize: maxSizeMb }));
      return Upload.LIST_IGNORE;
    }
    
    return true;
  };

  // Handle file change
  const handleChange = ({ fileList }: { fileList: UploadFile[] }) => {
    // Keep only the latest file
    setFileList(fileList.slice(-1));
  };

  return (
    <CustomModal
      title={t('upload_absence_proof')}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleUpload}
      loading={loading}
      saveText={t('upload')}
    >
      <div className="mb-3">
        <Alert
          message={t('upload_proof_instructions')}
          description={t('upload_proof_description', { maxSize: maxSizeMb })}
          type="info"
          showIcon
        />
      </div>
      
      <Dragger
        name="file"
        multiple={false}
        fileList={fileList}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        customRequest={({ onSuccess }) => {
          // Prevent actual upload, we'll handle it manually
          if (onSuccess) onSuccess({} as any);
        }}
        accept=".pdf"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('click_or_drag_file')}</p>
        <p className="ant-upload-hint">
          {t('only_pdf_files_supported')}
        </p>
      </Dragger>
    </CustomModal>
  );
};

export default AbsenceProofUploadModal;
